import type { TiptapDoc } from './types';
import type { RichEditorPreset } from '@odo/shared-ui';

function p(text: string): { type: 'paragraph'; content: { type: 'text'; text: string }[] } {
    return { type: 'paragraph', content: [{ type: 'text', text }] };
}

type InlineRun = string | { text: string; bold?: boolean; italic?: boolean; href?: string };

function inlineToNode(part: InlineRun): unknown {
    if (typeof part === 'string') return { type: 'text', text: part };
    const marks: Array<{ type: string; attrs?: Record<string, unknown> }> = [];
    if (part.bold) marks.push({ type: 'bold' });
    if (part.italic) marks.push({ type: 'italic' });
    if (part.href) marks.push({ type: 'link', attrs: { href: part.href } });
    return marks.length > 0
        ? { type: 'text', text: part.text, marks }
        : { type: 'text', text: part.text };
}

function pMix(parts: InlineRun[]): { type: 'paragraph'; content: unknown[] } {
    return { type: 'paragraph', content: parts.map(inlineToNode) };
}

function bulletMix(items: InlineRun[][]) {
    return listMix('bulletList', items);
}

function orderedMix(items: InlineRun[][]) {
    return listMix('orderedList', items);
}

function listMix(type: 'bulletList' | 'orderedList', items: InlineRun[][]) {
    return {
        type,
        content: items.map((parts) => ({
            type: 'listItem',
            content: [{ type: 'paragraph', content: parts.map(inlineToNode) }]
        }))
    };
}

function h(level: 1 | 2 | 3, text: string) {
    return { type: 'heading', attrs: { level }, content: [{ type: 'text', text }] };
}

function bullet(items: string[]) {
    return {
        type: 'bulletList',
        content: items.map((text) => ({
            type: 'listItem',
            content: [p(text)]
        }))
    };
}

// ---- Special-consent presets ------------------------------------------------
//
// Three age-specific variants surfaced as empty-state cards on the consent
// editor. Each variant opens with a bolded "Special Consent:" intro that
// frames simulation-vs-reality and age-appropriate biology, followed by
// shared paragraphs on behavioural readiness and ODO's consultative scope.

const CONSULTATIVE_OPINION_TEXT =
    'This diagnostic report is provided as a consultative opinion to support your clinical judgment and decision-making as the treating dentist of record. You remain solely responsible for reviewing this report, determining its applicability to the individual patient, obtaining informed consent where required, and managing all aspects of the patient’s diagnosis, treatment, follow-up, and referrals in accordance with your professional obligations and local regulations.';

const PEDIATRIC_BEHAVIORAL_TEXT =
    'Orthodontic treatment is unlike dental procedures in many ways. It’s important to assess the personality/behavioral style of a patient (and especially the parent!) and how they will accept “zigs and zags” in treatment experience (deviations from original expectations) as well as the expected “speed bumps” along the way as there are more of them with pediatric treatment plans. At ODO, we don’t have this firsthand opportunity to gauge this aspect of a patient assessment, so we bring it up as it’s critical for your experience as well as that of your team. When evaluating a child or teen’s readiness for a successful orthodontic experience, assess behavioural readiness for aligner therapy from multiple perspectives. Consider the patient’s motivation, maturity level, oral hygiene habits, attentiveness during instructions, prior compliance with removable appliances, and realistic expectations. Carefully assess parental support, accountability, and willingness to reinforce instructions at home. Exercise caution in cases with low engagement, chaotic routines, frequent snacking habits, poor hygiene, or limited parental involvement. When multiple risk indicators are present, consider recommending fixed appliances over aligners and/or determining whether the patient is an appropriate candidate for treatment within the practice.';

const ADULT_BEHAVIORAL_TEXT =
    'Orthodontic treatment is unlike dental procedures in many ways. It’s important to assess the personality/behavioral style of a patient and how they will accept “zigs and zags” in treatment experience (deviations from original expectations) as well as the expected “speed bumps” along the way. At ODO, we don’t have this firsthand opportunity to gauge this aspect of patient assessment, so we bring it up as it’s critical for your experience as well as that of your team. When evaluating an adult patient’s readiness for a successful orthodontic experience, assess behavioral readiness for aligner therapy from multiple perspectives. Look for indicators such as intrinsic motivation, an organized lifestyle, good oral hygiene, attentiveness during instructions, prior compliance with removable appliances, and realistic expectations. Exercise caution in cases with low engagement, chaotic routines, frequent snacking habits, or poor hygiene. When multiple risk indicators are present, consider recommending fixed appliances over aligners and/or determining whether the patient is an appropriate candidate for treatment within the practice.';

const CONSENT_CHILDREN_TIPTAP: TiptapDoc = {
    type: 'doc',
    content: [
        p(
            'It’s important to remember that the digital simulation is just that, a simulation. Please discuss risks, benefits, and alternatives with your patient. Compliance with aligner wear instructions is critical to treatment success. Based on the patient’s age, mandibular response should be good if compliance is as required.'
        ),
        p(PEDIATRIC_BEHAVIORAL_TEXT),
        p(CONSULTATIVE_OPINION_TEXT)
    ]
};

const CONSENT_TEENS_TIPTAP: TiptapDoc = {
    type: 'doc',
    content: [
        p(
            'It’s important to remember that the digital simulation is just that, a simulation. Please discuss risks, benefits, and alternatives with your patient. Based on the patient’s age, mandibular response MAY be good if compliance is as required, but at this age, we may be running out of growth. The patient may experience some spontaneous correction, but it can’t be guaranteed.'
        ),
        p(PEDIATRIC_BEHAVIORAL_TEXT),
        p(CONSULTATIVE_OPINION_TEXT)
    ]
};

const CONSENT_ADULTS_TIPTAP: TiptapDoc = {
    type: 'doc',
    content: [
        p(
            'It’s important to remember that the digital simulation is just that, a simulation. Please discuss risks, benefits, and alternatives with your patient. Compliance with aligner wear instructions is critical to treatment success. Based on the patient’s age and lack of growth, mandibular response may or may not be expected to occur. That said, even though orthopedic correction in adults is unlikely, some spontaneous correction can occur even without growth after archforms are coordinated and excess vertical overbite is corrected.'
        ),
        p(
            'Please confirm that you believe the periodontal status is stable and orthodontic treatment can be initiated. We will monitor increases in recession via Dental Monitoring.'
        ),
        p(ADULT_BEHAVIORAL_TEXT),
        p(CONSULTATIVE_OPINION_TEXT)
    ]
};

export const SPECIAL_CONSENT_PRESETS: RichEditorPreset[] = [
    {
        id: 'children',
        label: 'Children (3–15)',
        description:
            'Strong mandibular response expected with compliance; emphasizes parental support.',
        doc: CONSENT_CHILDREN_TIPTAP
    },
    {
        id: 'teens',
        label: 'Teens (16–18)',
        description:
            'Mandibular response may slow at the tail end of growth; spontaneous correction not guaranteed.',
        doc: CONSENT_TEENS_TIPTAP
    },
    {
        id: 'adults',
        label: 'Adults (19+)',
        description: 'No further growth expected; includes periodontal stability check.',
        doc: CONSENT_ADULTS_TIPTAP
    }
];

export const DEFAULT_ORTHOPULSE_RECOMMENDATION_TIPTAP: TiptapDoc = {
    type: 'doc',
    content: [
        p(
            'We recommend Orthopulse for this case to accelerate treatment and improve outcome predictability.'
        )
    ]
};

// ---- Orthopulse status presets -----------------------------------------------

const ORTHOPULSE_MANDATORY_TIPTAP: TiptapDoc = {
    type: 'doc',
    content: [
        p(
            "Orthopulse is mandatory for this case. Given the complexity of the planned tooth movements, and the risk mitigation that Orthopulse provides, it is required for ODO's consultation on this case."
        )
    ]
};

const ORTHOPULSE_NOT_NEEDED_TIPTAP: TiptapDoc = {
    type: 'doc',
    content: [p('Orthopulse is not needed for this case.')]
};

export const ORTHOPULSE_STATUS_PRESET_DOCS: Record<
    'mandatory' | 'recommended' | 'not_needed',
    TiptapDoc
> = {
    mandatory: ORTHOPULSE_MANDATORY_TIPTAP,
    recommended: DEFAULT_ORTHOPULSE_RECOMMENDATION_TIPTAP,
    not_needed: ORTHOPULSE_NOT_NEEDED_TIPTAP
};

/**
 * List prices to quote when Orthopulse is NOT on the case, so no resolved fee
 * line exists to read a real figure from.
 *
 * These are the only hard-coded Orthopulse prices left in the app. Whenever
 * Orthopulse IS on the case, the cost shown comes from the resolved
 * `fees.addons` line instead - which the product catalog prices per market
 * off `practice.country` - so this fallback is a last resort, not the normal
 * path. Keep it in step with the OrthoPulse product catalog.
 */
export const ORTHOPULSE_LIST_PRICE_FALLBACK = '$375 USD (US) / $500 CAD (Canada)';

/**
 * "About Orthopulse" info page.
 *
 * Takes the cost label rather than hard-coding one: the same PDF's fee table
 * already prints a per-market price resolved from the product catalog, so a
 * fixed "$425" in this prose contradicted the fee table for any non-US
 * practice. Callers pass the resolved figure when there is one and
 * ORTHOPULSE_LIST_PRICE_FALLBACK when there is not.
 */
export function buildOrthopulseInfoTiptap(costLabel: string): TiptapDoc {
    return {
        type: 'doc',
        content: [
            pMix([
                { text: 'Orthopulse', bold: true },
                ' is a mouthguard-style device worn 20 minutes per day. It works via ',
                { text: 'photobiomodulation', italic: true },
                ': infrared light penetrates bone cells and enhances ATP production in the mitochondria, optimizing cellular metabolism and bone remodeling.'
            ]),

            h(3, 'Clinical benefits'),
            bullet([
                'Speeds up treatment in suitable patients and keeps progress on track in more difficult cases.',
                'Enhances periodontal stability when bone or perio condition is a concern.',
                'Reduces pain (already minimal with aligners) and inflammation.'
            ]),

            h(3, 'Practical notes'),
            bulletMix([
                [
                    { text: `Cost: ${costLabel}`, bold: true },
                    ', a savings of $400 over retail. Typically passed on to the patient.'
                ],
                ['Instructions on use are provided to the patient.'],
                [
                    'We strongly recommend the decision to prescribe Orthopulse rests with the doctor, unless the goal is speed alone.'
                ],
                [
                    'Adding it into the treatment fee rather than offering as an ',
                    { text: '"option"', italic: true },
                    ' is highly recommended.'
                ]
            ]),

            pMix([
                'For more information, visit ',
                {
                    text: 'orthopulse.com',
                    href: 'https://www.orthopulse.com',
                    bold: true
                },
                '.'
            ])
        ]
    };
}

export const STATIC_TEMPLATE_OPTIONS_TIPTAP: TiptapDoc = {
    type: 'doc',
    content: [
        pMix([{ text: 'Important - please read before approving.', bold: true }]),

        h(3, 'Why this matters'),
        p(
            'Normally the full aligner series ships alongside the attachment template, so any flash or attachment irregularity from bonding day isn’t reflected in those trays. Both are among the most common reasons we see premature refinement appointments - which extend treatment time and add unwanted visits.'
        ),

        h(3, 'The Starter Aligners option'),
        pMix([
            'To mitigate this, we offer to ship the attachment template and ',
            { text: 'three Starter Aligners only', bold: true },
            ' (2-3 week turnaround). You bond attachments, scan after placement, and submit for a refinement. We then ship ',
            { text: 'Active Aligners', bold: true },
            ' built around the actual attachment positions and any minor irregularities, so the trays fit precisely and early refinements stay rare.'
        ]),
        p(
            'Scanning at this stage is itself diagnostic for flash and captures the anatomy as it really sits - not as the simulation predicted it.'
        ),

        h(3, 'What we need from you'),
        pMix([
            'When approving this case, please tell us if you’d like ',
            { text: 'Starter Aligners', bold: true },
            '. If you don’t specify, we’ll continue with the standard full-series delivery.'
        ]),
        pMix([
            'For questions or feedback that don’t warrant a Zoom meeting, please use the ',
            { text: 'conversation bubble', bold: true },
            ' next to the patient’s name on the ODO Dashboard - that way the thread stays attached to the case.'
        ])
    ]
};

export const STATIC_NEXT_STEPS_TIPTAP: TiptapDoc = {
    type: 'doc',
    content: [
        orderedMix([
            [
                'Review the case simulation. You may use this to articulate the treatment plan to your patient.'
            ],
            [
                'For any questions or comments on the case design, please use the chat bubble adjacent to your patient’s name on your ODO Dashboard. ',
                { text: 'Do not', bold: true },
                ' use the notes on The Approver software; that channel is not monitored by our team.'
            ]
        ]),

        h(3, 'Please review the following information'),
        bulletMix([
            [
                {
                    text: 'What are integrated hooks, bite ramps, caplin hooks, and how they are used.',
                    href: 'https://drive.google.com/file/d/1bF5n-2y7tEPdhrnWla8pEtFFqce4b5R_/view?usp=sharing'
                }
            ]
        ])
    ]
};

export const EMPTY_TIPTAP_DOC: TiptapDoc = {
    type: 'doc',
    content: [{ type: 'paragraph' }]
};
